/**
 * What the news desk hears (newsDesk.ts): the game's events turned into stories. Wired by the audio
 * system; the radio asks the desk for a bulletin as a news, travel or DJ-link item comes on air.
 *
 *  - a car the player takes (model, colour, district, whether the driver was pulled out; police cars are left
 *    out: that's its own story);
 *  - a chase starting (heat 2+; level 3+ is the big one) and the law losing the player after one;
 *  - a police car forced off the road and out of the chase (the heat service's `unitDown`);
 *  - an arrest; a story job, street job, export order or car job done (the job's own bulletin by id, or
 *    where it ended and nothing more); a night race finished;
 *  - a mayhem challenge with something to show for it (src/activities);
 *  - online: the shard's world events and turf news, through the `worldNews` event that src/online-ui
 *    emits (kind, stage and district only — the desk writes its own words), and the Kessmouth week's
 *    modifier and honours (WP-X5: the modifier, its district or grade, whether your firm made the book).
 */
import type { GameContext } from '../../core/context';
import type { V2 } from '../../core/math';
import { DISTRICTS, districtAt } from '../../world/lowBorough/data';
import { colourName, worldNewsText, type NewsDesk } from './newsDesk';

declare module '../../core/events' {
  interface GameEvents {
    /**
     * Online world news for the radio (src/online-ui emits it as server news arrives): a world event
     * (`source: 'event'`, its kind and stage) or turf news (`'territory'`, its kind). No names, no text.
     */
    worldNews: { source: 'event' | 'territory' | 'week'; kind: string; stage: string; district: string | null; x?: number; z?: number; crackdown?: boolean; grade?: number; you?: boolean };
  }
}

/** A district id or a position as the name a newsreader says. */
export function districtNameOf(where: string | V2 | null | undefined): string {
  const id = typeof where === 'string' ? where : where ? districtAt(where) : null;
  return DISTRICTS.find((d) => d.id === id)?.name ?? 'the borough';
}

export function wireNewsDesk(ctx: GameContext, desk: NewsDesk): () => void {
  const now = (): number => ctx.state.time;
  const here = (): string => districtNameOf(ctx.services.tryGet('player')?.pos ?? null);
  let chased = false;
  let worldN = 0;
  const offs = [
    ctx.events.on('vehicleStolen', (e) => {
      if (!e.byPlayer || e.isPolice) return;
      const v = ctx.services.tryGet('vehicles')?.get(e.vehicleId);
      if (!v) return;
      desk.note({ kind: 'theft', model: v.spec.displayName, colour: colourName(v.paint), district: districtNameOf(e.pos), ...(e.carjack ? { carjack: true } : {}) }, now());
    }),
    ctx.events.on('heatChanged', (e) => {
      // A chase at 2, and again when it turns into the big one at 3 (a different story, said differently).
      if ((e.level >= 2 && e.previous < 2) || (e.level >= 3 && e.previous < 3)) {
        chased = true;
        desk.note({ kind: 'chase', district: here(), level: e.level }, now());
      }
      if (e.level === 0 && chased) {
        chased = false;
        desk.note({ kind: 'gotAway', district: here() }, now());
      }
    }),
    ctx.events.on('unitDown', (e) => {
      desk.note({ kind: 'takedown', district: e.pos ? districtNameOf(e.pos) : here() }, now());
    }),
    ctx.events.on('playerBusted', (e) => {
      chased = false;
      desk.note({ kind: 'arrest', district: districtNameOf(e.pos) }, now());
    }),
    ctx.events.on('missionCompleted', (e) => {
      const kind = ctx.services.tryGet('missions')?.board().find((b) => b.id === e.id)?.kind;
      if (kind === 'story' || kind === 'street' || kind === 'export' || e.id.startsWith('car-job-')) desk.note({ kind: 'job', district: here(), id: e.id }, now());
      else if (e.id === 'street-race') desk.note({ kind: 'race', district: here(), won: true }, now());
    }),
    ctx.events.on('missionFailed', (e) => {
      if (e.id === 'street-race' && /Beaten to the finish/.test(e.reason)) desk.note({ kind: 'race', district: here(), won: false }, now());
    }),
    ctx.events.on('mayhemFinished', (e) => {
      if (e.done <= 0) return;
      desk.note({ kind: 'mayhem', what: e.kind, district: e.district, done: e.done, tier: e.tier }, now());
    }),
    ctx.events.on('worldNews', (e) => {
      const district = districtNameOf(e.district ?? (e.x !== undefined && e.z !== undefined ? { x: e.x, z: e.z } : null));
      const text = worldNewsText({ source: e.source, kind: e.kind, stage: e.stage, district, ...(e.district ? { districtId: e.district } : {}), ...(e.crackdown ? { crackdown: true } : {}), ...(e.grade !== undefined ? { grade: e.grade } : {}), ...(e.you ? { you: true } : {}) }, worldN++);
      if (text) desk.note({ kind: 'world', text }, now());
    }),
    ctx.events.on('newGameStarted', () => {
      chased = false;
      desk.clear();
    }),
  ];
  return () => {
    for (const off of offs) off();
  };
}
